import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Figma (node 21123:292666 — Card):
 * Container: bg-card, border border-border, rounded-xl (14px), shadow-sm, overflow-clip,
 *   flex flex-col items-start
 * Size=default: Header p-4, gap-2 (outer), text block gap-1; Title text-base/medium/leading-6; Content px-4 pb-4
 * Size=sm: Header p-3, gap-2 (outer), text block gap-1; Title text-sm/medium/leading-5; Content px-3 pb-3
 * Description: text-sm/normal/leading-5 text-muted-foreground (both sizes)
 * Footer: flex flex-col items-start w-full (no padding)
 */

type CardSize = 'default' | 'sm'

const CardSizeContext = React.createContext<CardSize>('default')

// --- Card ---

export interface IntuiCardProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: CardSize
}

const IntuiCard = React.forwardRef<HTMLDivElement, IntuiCardProps>(
  ({ className, size = 'default', ...props }, ref) => (
    <CardSizeContext.Provider value={size}>
      <div
        ref={ref}
        className={cn(
          'bg-card border border-border text-card-foreground rounded-xl shadow-sm overflow-clip flex flex-col items-start',
          className
        )}
        {...props}
      />
    </CardSizeContext.Provider>
  )
)
IntuiCard.displayName = 'IntuiCard'

// --- CardHeader ---

const IntuiCardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const size = React.useContext(CardSizeContext)

  // Separate title/description children from action children
  // Figma: header gap-2, inner text block gap-1
  const childArray = React.Children.toArray(children)
  const textChildren: React.ReactNode[] = []
  const otherChildren: React.ReactNode[] = []
  childArray.forEach((child) => {
    if (
      React.isValidElement(child) &&
      typeof child.type !== 'string' &&
      'displayName' in child.type &&
      ((child.type as { displayName?: string }).displayName === 'IntuiCardTitle' ||
        (child.type as { displayName?: string }).displayName === 'IntuiCardDescription')
    ) {
      textChildren.push(child)
    } else {
      otherChildren.push(child)
    }
  })

  return (
    <div
      ref={ref}
      className={cn(
        'flex gap-2 items-start w-full',
        size === 'sm' ? 'p-3' : 'p-4',
        className
      )}
      {...props}
    >
      {textChildren.length > 0 ? (
        <div className="flex flex-1 flex-col gap-1 min-w-0">
          {textChildren}
        </div>
      ) : null}
      {otherChildren}
    </div>
  )
})
IntuiCardHeader.displayName = 'IntuiCardHeader'

// --- CardTitle ---

const IntuiCardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const size = React.useContext(CardSizeContext)
  return (
    <p
      ref={ref}
      className={cn(
        'font-medium text-card-foreground',
        size === 'sm' ? 'text-sm leading-5' : 'text-base leading-6',
        className
      )}
      {...props}
    />
  )
})
IntuiCardTitle.displayName = 'IntuiCardTitle'

// --- CardDescription ---

const IntuiCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm font-normal leading-5 text-muted-foreground', className)}
    {...props}
  />
))
IntuiCardDescription.displayName = 'IntuiCardDescription'

// --- CardContent ---

const IntuiCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const size = React.useContext(CardSizeContext)
  return (
    <div
      ref={ref}
      className={cn(
        'w-full',
        size === 'sm' ? 'px-3 pb-3' : 'px-4 pb-4',
        className
      )}
      {...props}
    />
  )
})
IntuiCardContent.displayName = 'IntuiCardContent'

// --- CardFooter ---

const IntuiCardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col items-start w-full', className)}
    {...props}
  />
))
IntuiCardFooter.displayName = 'IntuiCardFooter'

export {
  IntuiCard,
  IntuiCardHeader,
  IntuiCardTitle,
  IntuiCardDescription,
  IntuiCardContent,
  IntuiCardFooter,
}
